import { McpDefinition } from "./mcpDefinition.ts";
import {
  DEFAULT_PREVIEW_APPEARANCE,
  DEFAULT_PREVIEW_ZOOM_FACTOR,
  FILL_PREVIEW_VIEWPORT,
  PreviewAppearancePreference,
  PreviewViewportSetting,
  PreviewZoomFactor,
} from "./preview.ts";
import { BrowserProfile, BrowserProfileId, DEFAULT_BROWSER_PROFILE_ID } from "./browserProfile.ts";
import * as Effect from "effect/Effect";
import * as Duration from "effect/Duration";
import * as Schema from "effect/Schema";
import * as SchemaTransformation from "effect/SchemaTransformation";
import { EnvironmentId, ProjectId, TrimmedNonEmptyString, TrimmedString } from "./baseSchemas.ts";
import {
  DEFAULT_GIT_TEXT_GENERATION_MODEL,
  DEFAULT_MODEL,
  ProviderOptionSelections,
} from "./model.ts";
import { EnvironmentMachineKind, ScopedThreadRef } from "./environment.ts";
import { SshEnvironmentTarget } from "./sshEnvironment.ts";
import { PullRequestMergeMethod } from "./sourceControl.ts";
import { ModelSelection } from "./orchestration.ts";
import { ProviderInstanceConfig, ProviderInstanceId } from "./providerInstance.ts";
import {
  DEFAULT_SNAP_SHOT_SHORTCUT,
  DEFAULT_SNAP_SHOT_SOUND,
  SnapShotShortcut,
  SnapShotSound,
} from "./snapShot.ts";

// ── Client Settings (local-only) ───────────────────────────────

export const TimestampFormat = Schema.Literals(["locale", "12-hour", "24-hour"]);
export type TimestampFormat = typeof TimestampFormat.Type;
export const DEFAULT_TIMESTAMP_FORMAT: TimestampFormat = "locale";

export const SidebarProjectSortOrder = Schema.Literals(["updated_at", "created_at", "manual"]);
export type SidebarProjectSortOrder = typeof SidebarProjectSortOrder.Type;
export const DEFAULT_SIDEBAR_PROJECT_SORT_ORDER: SidebarProjectSortOrder = "updated_at";

export const SidebarThreadSortOrder = Schema.Literals(["updated_at", "created_at"]);
export type SidebarThreadSortOrder = typeof SidebarThreadSortOrder.Type;
export const DEFAULT_SIDEBAR_THREAD_SORT_ORDER: SidebarThreadSortOrder = "updated_at";

/**
 * How much of a response reaches the client as it arrives. `turn` delivers the
 * finished message, `paragraph` releases each completed block, and `token`
 * forwards every delta — the loudest option, kept for readers who want it.
 */
export const ResponseStreamingMode = Schema.Literals(["turn", "paragraph", "token"]);
export type ResponseStreamingMode = typeof ResponseStreamingMode.Type;
export const DEFAULT_RESPONSE_STREAMING_MODE: ResponseStreamingMode = "paragraph";

/** What the quit shortcut does before it quits. */
export const QuitConfirmationMode = Schema.Literals(["direct", "hold", "double-press"]);
export type QuitConfirmationMode = typeof QuitConfirmationMode.Type;

/** Whether a prompt sent mid-turn waits its turn or redirects the running one. */
export const FollowUpBehavior = Schema.Literals(["queue", "steer"]);
export type FollowUpBehavior = typeof FollowUpBehavior.Type;

export const MIN_AUTO_SETTLE_AFTER_DAYS = 1;
export const MAX_AUTO_SETTLE_AFTER_DAYS = 90;
export const AUTO_SETTLE_DEFAULT_DAYS = 3;
export const AutoSettleAfterDays = Schema.Int.check(
  Schema.isBetween({
    minimum: MIN_AUTO_SETTLE_AFTER_DAYS,
    maximum: MAX_AUTO_SETTLE_AFTER_DAYS,
  }),
);
export type AutoSettleAfterDays = typeof AutoSettleAfterDays.Type;

/**
 * One shared answer to "how eagerly may the Environment work when nobody is
 * asking". Each background job keeps its own interval; this gates whether the
 * interval is honoured, stretched, or skipped while the host is idle.
 */
export const BackgroundActivityProfile = Schema.Literals([
  "balanced",
  "performance",
  "battery-saver",
]);
export type BackgroundActivityProfile = typeof BackgroundActivityProfile.Type;
export const DEFAULT_BACKGROUND_ACTIVITY_PROFILE: BackgroundActivityProfile = "balanced";

/** How a diff lays its two sides out. The diff toolbar writes this too. */
export const DiffLayout = Schema.Literals(["stacked", "split"]);
export type DiffLayout = typeof DiffLayout.Type;

export const SidebarProjectGroupingMode = Schema.Literals([
  "repository",
  "repository_path",
  "separate",
]);
export type SidebarProjectGroupingMode = typeof SidebarProjectGroupingMode.Type;
export const DEFAULT_SIDEBAR_PROJECT_GROUPING_MODE: SidebarProjectGroupingMode = "repository";
export const MIN_SIDEBAR_THREAD_PREVIEW_COUNT = 1;
export const MAX_SIDEBAR_THREAD_PREVIEW_COUNT = 15;
export const SidebarThreadPreviewCount = Schema.Int.check(
  Schema.isBetween({
    minimum: MIN_SIDEBAR_THREAD_PREVIEW_COUNT,
    maximum: MAX_SIDEBAR_THREAD_PREVIEW_COUNT,
  }),
);
export type SidebarThreadPreviewCount = typeof SidebarThreadPreviewCount.Type;
export const DEFAULT_SIDEBAR_THREAD_PREVIEW_COUNT: SidebarThreadPreviewCount = 6;

export const BrowserPreferences = Schema.Struct({
  viewport: PreviewViewportSetting,
  zoom: Schema.Literals([0.5, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 2]),
  appearance: Schema.Literals(["system", "light", "dark"]),
  recordingFrameRate: Schema.Literals([12, 30, 60]),
  autoShow: Schema.Boolean,
});
export type BrowserPreferences = typeof BrowserPreferences.Type;

/**
 * Font size preferences, in CSS pixels. The ranges are deliberately narrow:
 * the interface size scales every rem-based dimension in the app, so the
 * bounds keep layouts intact rather than offering unusable extremes.
 */
export const MIN_INTERFACE_FONT_SIZE = 12;
export const MAX_INTERFACE_FONT_SIZE = 20;
export const InterfaceFontSize = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_INTERFACE_FONT_SIZE, maximum: MAX_INTERFACE_FONT_SIZE }),
);
export type InterfaceFontSize = typeof InterfaceFontSize.Type;
export const DEFAULT_INTERFACE_FONT_SIZE: InterfaceFontSize = 16;

export const MIN_PROMPT_FONT_SIZE = 12;
export const MAX_PROMPT_FONT_SIZE = 20;
export const PromptFontSize = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_PROMPT_FONT_SIZE, maximum: MAX_PROMPT_FONT_SIZE }),
);
export type PromptFontSize = typeof PromptFontSize.Type;
export const DEFAULT_PROMPT_FONT_SIZE: PromptFontSize = 14;

export const MIN_CODE_FONT_SIZE = 10;
export const MAX_CODE_FONT_SIZE = 18;
export const CodeFontSize = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_CODE_FONT_SIZE, maximum: MAX_CODE_FONT_SIZE }),
);
export type CodeFontSize = typeof CodeFontSize.Type;
export const DEFAULT_CODE_FONT_SIZE: CodeFontSize = 13;

export const MIN_TERMINAL_FONT_SIZE = 8;
export const MAX_TERMINAL_FONT_SIZE = 20;
export const TerminalFontSize = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_TERMINAL_FONT_SIZE, maximum: MAX_TERMINAL_FONT_SIZE }),
);
export type TerminalFontSize = typeof TerminalFontSize.Type;
export const DEFAULT_TERMINAL_FONT_SIZE: TerminalFontSize = 12;

/**
 * A user-chosen font family (a single name or a comma-separated list). Empty
 * means "use the app default"; clients compose their own fallback stacks.
 */
export const FontFamilyPreference = Schema.String.check(Schema.isMaxLength(200));
export type FontFamilyPreference = typeof FontFamilyPreference.Type;

/**
 * The environment's theme, set with `afar-research theme set <id>`. Each client
 * applies it once per value - live when connected, on its next connect
 * otherwise - so setting it switches every client, while a theme a user picks
 * in Settings afterwards sticks until the next set. Empty means "no
 * environment theme", which is also how it is cleared.
 */
export const DefaultThemePreference = Schema.String.check(Schema.isMaxLength(64));
// Deliberately absent from ServerSettingsPatch: `theme set` checks that an id
// is syntactically valid and actually resolvable, and a generic RPC patch
// would let a client write a theme no client can resolve, bypassing both.
export type DefaultThemePreference = typeof DefaultThemePreference.Type;

/** Additions and deletions are colored for the user, not for the diff format. */
export const DiffColorScheme = Schema.Literals(["red-green", "blue-orange"]);
export type DiffColorScheme = typeof DiffColorScheme.Type;
export const DEFAULT_DIFF_COLOR_SCHEME: DiffColorScheme = "red-green";

export const MIN_APPEARANCE_CONTRAST = 50;
export const MAX_APPEARANCE_CONTRAST = 200;
export const AppearanceContrast = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_APPEARANCE_CONTRAST, maximum: MAX_APPEARANCE_CONTRAST }),
);
export type AppearanceContrast = typeof AppearanceContrast.Type;
export const DEFAULT_APPEARANCE_CONTRAST: AppearanceContrast = 100;

export const MIN_GLASS_OPACITY = 0;
export const MAX_GLASS_OPACITY = 100;
export const GlassOpacity = Schema.Int.check(
  Schema.isBetween({ minimum: MIN_GLASS_OPACITY, maximum: MAX_GLASS_OPACITY }),
);
export type GlassOpacity = typeof GlassOpacity.Type;
export const DEFAULT_GLASS_OPACITY: GlassOpacity = 80;

export const PROJECT_ICON_NAMES = [
  "folder",
  "code",
  "terminal",
  "globe",
  "rocket",
  "book",
  "flask",
  "heart",
  "star",
  "music",
  "camera",
  "palette",
] as const;

export const ProjectIconValue = Schema.Union([
  Schema.String.check(
    Schema.isMaxLength(100000),
    Schema.isPattern(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/),
  ),
  Schema.Literals(PROJECT_ICON_NAMES.map((name) => `icon:${name}`)),
  Schema.String.check(Schema.isMaxLength(64), Schema.isPattern(/^emoji:\p{RGI_Emoji}$/v)),
]);

const ProjectPresentation = Schema.Struct({
  icon: Schema.optionalKey(ProjectIconValue),
  workspace: Schema.optionalKey(Schema.Literals(["local", "worktree"])),
});

export const MIN_PANEL_ANIMATION_DURATION_MS = 0;
export const MAX_PANEL_ANIMATION_DURATION_MS = 400;
/** Panel open and close timing, in milliseconds. Zero means no animation. */
export const PanelAnimationDurationMs = Schema.Int.check(
  Schema.isBetween({
    minimum: MIN_PANEL_ANIMATION_DURATION_MS,
    maximum: MAX_PANEL_ANIMATION_DURATION_MS,
  }),
);
export type PanelAnimationDurationMs = typeof PanelAnimationDurationMs.Type;

/**
 * Defaults for the in-app browser, applied whenever a tab is opened without an
 * explicit viewport, zoom or appearance — by the user opening a browser tab, or
 * by an agent calling `preview_open` with no size. These live in client
 * settings because the Chromium guest they configure is desktop-local
 * (ADR-0118).
 */
export const DEFAULT_BROWSER_VIEWPORT: PreviewViewportSetting = FILL_PREVIEW_VIEWPORT;
export const DEFAULT_BROWSER_AUTO_SHOW_FLOATING_PREVIEW = true;
export const BROWSER_RECORDING_FRAME_RATES = [30, 60] as const;
export const BrowserRecordingFrameRate = Schema.Literals(BROWSER_RECORDING_FRAME_RATES);
export type BrowserRecordingFrameRate = typeof BrowserRecordingFrameRate.Type;
export const DEFAULT_BROWSER_RECORDING_FRAME_RATE: BrowserRecordingFrameRate = 30;
/**
 * Where a clicked link goes: the OS default browser, or a tab in the in-app
 * browser beside the thread. "system" is the default because that is what every
 * link did before the setting existed.
 */
export const BrowserLinkTarget = Schema.Literals(["system", "app"]);
export type BrowserLinkTarget = typeof BrowserLinkTarget.Type;
export const DEFAULT_BROWSER_LINK_TARGET: BrowserLinkTarget = "system";

export const LoadBalancingPreferenceSetting = Schema.Literals([
  "prefer",
  "normal",
  "less-often",
  "manual-only",
]);
export type LoadBalancingPreferenceSetting = typeof LoadBalancingPreferenceSetting.Type;

export const GitHubRoutingPermissionSetting = Schema.Struct({
  environmentId: EnvironmentId,
  /** The saved destination the grant was made against; a grant that no longer matches is void. */
  connectionKey: Schema.String,
  permission: Schema.Literals(["off", "read", "read-write"]),
});
export type GitHubRoutingPermissionSetting = typeof GitHubRoutingPermissionSetting.Type;

/**
 * One Environment this device reaches over SSH.
 *
 * The destination is stored, not an address: an SSH Environment answers on a loopback port that
 * only exists while a forward is open, so there is nothing durable to remember about where it
 * answers — only about how to get there.
 */
export const SshConnectionSetting = Schema.Struct({
  environmentId: EnvironmentId,
  label: TrimmedNonEmptyString,
  target: SshEnvironmentTarget,
});
export type SshConnectionSetting = typeof SshConnectionSetting.Type;

/**
 * One Environment this device paired with by link, over whatever network reaches it — a tailnet
 * name from Tailscale Serve, a LAN address.
 *
 * The session is the Environment Access the destination issued when its one-time grant was
 * redeemed, and is what every connection presents; it is never the grant, which is spent. The
 * destination can end it from its Authorized clients list, after which this device must pair again.
 */
export const PairedConnectionSetting = Schema.Struct({
  environmentId: EnvironmentId,
  label: TrimmedNonEmptyString,
  httpBaseUrl: TrimmedNonEmptyString,
  wsBaseUrl: TrimmedNonEmptyString,
  session: TrimmedNonEmptyString,
});
export type PairedConnectionSetting = typeof PairedConnectionSetting.Type;

export const ClientSettingsSchema = Schema.Struct({
  browserDefaultViewport: PreviewViewportSetting.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_BROWSER_VIEWPORT)),
  ),
  browserDefaultZoomFactor: PreviewZoomFactor.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PREVIEW_ZOOM_FACTOR)),
  ),
  browserDefaultAppearance: PreviewAppearancePreference.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PREVIEW_APPEARANCE)),
  ),
  browserRecordingFrameRate: BrowserRecordingFrameRate.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_BROWSER_RECORDING_FRAME_RATE)),
  ),
  browserLinkTarget: BrowserLinkTarget.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_BROWSER_LINK_TARGET)),
  ),
  browserAutoShowFloatingPreview: Schema.Boolean.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_BROWSER_AUTO_SHOW_FLOATING_PREVIEW)),
  ),
  panelAnimationDurationMs: PanelAnimationDurationMs.pipe(
    Schema.withDecodingDefault(Effect.succeed(0)),
  ),
  sidebarProjectScopeKey: Schema.optionalKey(Schema.NullOr(Schema.String)),
  projectPresentation: Schema.optionalKey(Schema.Record(Schema.String, ProjectPresentation)),
  browserPreferences: Schema.optionalKey(BrowserPreferences),
  appearanceContrast: AppearanceContrast.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_APPEARANCE_CONTRAST)),
  ),
  glassOpacity: GlassOpacity.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_GLASS_OPACITY)),
  ),
  diffColorScheme: DiffColorScheme.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_DIFF_COLOR_SCHEME)),
  ),
  fontSizeInterface: InterfaceFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_INTERFACE_FONT_SIZE)),
  ),
  fontSizePrompt: PromptFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROMPT_FONT_SIZE)),
  ),
  fontSizeCode: CodeFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_CODE_FONT_SIZE)),
  ),
  fontSizeTerminal: TerminalFontSize.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_TERMINAL_FONT_SIZE)),
  ),
  fontFamilySans: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  fontFamilyCode: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  fontFamilyComposer: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  fontFamilyTerminal: FontFamilyPreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  // Grayscale `-webkit-font-smoothing: antialiased` (thinner strokes);
  // disabling restores the platform's heavier default. No effect off macOS.
  fontSmoothing: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  // Legacy plan mode. The composer's Build/Plan toggle is off by default; this
  // restores it, along with the /plan and /default slash commands.
  planModeEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  // Legacy context window meter, hidden by default and restorable from Settings.
  contextWindowMeterEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  // Scrolling an existing thread settles the composer into its single line.
  // Losing focus never does.
  composerCollapseOnScroll: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  // Rich text is the default; users can opt out for literal Markdown editing.
  composerRichTextEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  sendShortcut: Schema.Literals(["enter", "mod-enter-multiline", "mod-enter"]).pipe(
    Schema.withDecodingDefault(Effect.succeed("enter")),
  ),
  showSkillsInSlashMenu: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  autoOpenPlanSidebar: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  confirmThreadArchive: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  confirmThreadDelete: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  confirmThreadUnpin: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  diffIgnoreWhitespace: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  diffFilesCollapsed: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  inAppNotificationsEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  proactivePanelsEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  followUpBehavior: FollowUpBehavior.pipe(
    Schema.withDecodingDefault(Effect.succeed("queue" as const)),
  ),
  confirmQuit: QuitConfirmationMode.pipe(
    Schema.withDecodingDefault(Effect.succeed("hold" as const)),
  ),
  diffLayout: DiffLayout.pipe(Schema.withDecodingDefault(Effect.succeed("stacked" as const))),
  environmentSettingsSelectionId: Schema.NullOr(EnvironmentId).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  // Model favorites. Historically keyed by provider kind, now
  // widened to `ProviderInstanceId` so users can favorite a specific model
  // on a custom provider instance (e.g. "Codex Personal · gpt-5") without
  // the UI collapsing it into the same bucket as the default Codex. The
  // widening is backward-compatible by construction: prior provider-kind
  // strings satisfy the `ProviderInstanceId` slug schema, so previously
  // persisted favorites decode unchanged and continue to point at the
  // default instance for their kind (because `defaultInstanceIdForDriver(kind)`
  // uses the same slug). The field name is kept as `provider` for storage
  // stability; new call sites should treat the value as an instance id.
  favorites: Schema.Array(
    Schema.Struct({
      provider: ProviderInstanceId,
      model: TrimmedNonEmptyString,
    }),
  ).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
  providerModelPreferences: Schema.Record(
    ProviderInstanceId,
    Schema.Struct({
      hiddenModels: Schema.Array(Schema.String).pipe(
        Schema.withDecodingDefault(Effect.succeed([])),
      ),
      modelOrder: Schema.Array(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
    }),
  ).pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  pinnedThreadRefs: Schema.Array(ScopedThreadRef).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  sidebarProjectGroupingMode: SidebarProjectGroupingMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_PROJECT_GROUPING_MODE)),
  ),
  sidebarProjectGroupingOverrides: Schema.Record(
    TrimmedNonEmptyString,
    SidebarProjectGroupingMode,
  ).pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  sidebarProjectSortOrder: SidebarProjectSortOrder.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_PROJECT_SORT_ORDER)),
  ),
  sidebarThreadSortOrder: SidebarThreadSortOrder.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_THREAD_SORT_ORDER)),
  ),
  sidebarThreadPreviewCount: SidebarThreadPreviewCount.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SIDEBAR_THREAD_PREVIEW_COUNT)),
  ),
  timestampFormat: TimestampFormat.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_TIMESTAMP_FORMAT)),
  ),
  snapShotEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  snapShotIncludeAccessibility: Schema.Boolean.pipe(
    Schema.withDecodingDefault(Effect.succeed(true)),
  ),
  snapShotShortcut: SnapShotShortcut.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SNAP_SHOT_SHORTCUT)),
  ),
  snapShotPlaySound: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  snapShotSound: SnapShotSound.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_SNAP_SHOT_SOUND)),
  ),
  snapShotFlash: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  snapShotAnimations: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  wordWrap: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  /**
   * Whether this client picks a machine for new threads by itself. Off by default: choosing for
   * the user is only welcome once they have said it is.
   */
  loadBalancingEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  /**
   * How much of the automatic share each Environment takes, by Environment ID. Absent means
   * Normal. Client-scoped because it describes the machines this device can reach.
   */
  loadBalancingPreferences: Schema.Record(Schema.String, LoadBalancingPreferenceSetting).pipe(
    Schema.withDecodingDefault(Effect.succeed({})),
  ),
  /**
   * Which Environments may use another's GitHub access, and how much of it. Each grant names the
   * destination it was made against, so re-pointing a Linked Computer voids it rather than
   * carrying the trust to a host the user never approved.
   */
  gitHubRoutingPermissions: Schema.Array(GitHubRoutingPermissionSetting).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  /**
   * Environments the user switched off on this device. Stored as the exceptions rather than as a
   * per-Environment flag so a machine that was never switched off needs no record, and one that
   * is forgotten leaves nothing behind.
   */
  disabledEnvironmentIds: Schema.Array(EnvironmentId).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  /**
   * The AFAR Connect computers this device uses, per AFAR Product Account. An account lists every
   * computer linked to it; this device lists only those it added, and offers the rest as available
   * in the Add environment dialog. Removing one from this device takes it out of here rather than
   * leaving the account's listing to bring it back. An account with no entry has not been listed
   * on this device yet: its first listing is taken as added, so what a device showed it keeps.
   */
  connectEnvironmentIdsByAccount: Schema.Record(Schema.String, Schema.Array(EnvironmentId)).pipe(
    Schema.withDecodingDefault(Effect.succeed({})),
  ),
  /**
   * Environments this device reaches over the user's own SSH.
   *
   * Kept per device rather than per account, because the destination is only meaningful where the
   * user's SSH configuration and keys are: an alias saved on one machine names nothing on
   * another. No credential is stored — a password the person types answers one attempt and is
   * never written down.
   */
  sshConnections: Schema.Array(SshConnectionSetting).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  /** Environments this device paired with by link. Per device, like the SSH ones. */
  pairedConnections: Schema.Array(PairedConnectionSetting).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
});
export type ClientSettings = typeof ClientSettingsSchema.Type;

export const DEFAULT_CLIENT_SETTINGS: ClientSettings = Schema.decodeSync(ClientSettingsSchema)({});

// ── Server Settings (server-authoritative) ────────────────────

export const ThreadEnvMode = Schema.Literals(["local", "worktree"]);
export type ThreadEnvMode = typeof ThreadEnvMode.Type;

const makeBinaryPathSetting = (fallback: string) =>
  TrimmedString.pipe(
    Schema.decodeTo(
      Schema.String,
      SchemaTransformation.transformOrFail({
        decode: (value) => Effect.succeed(value || fallback),
        encode: (value) => Effect.succeed(value),
      }),
    ),
    Schema.withDecodingDefault(Effect.succeed(fallback)),
  );

export type ProviderSettingsFormControl = "text" | "password" | "textarea" | "switch";

export interface ProviderSettingsFormAnnotation {
  readonly control?: ProviderSettingsFormControl | undefined;
  readonly placeholder?: string | undefined;
  readonly hidden?: boolean | undefined;
  readonly clearWhenEmpty?: "omit" | "persist" | undefined;
}

export interface ProviderSettingsFormSchemaAnnotation {
  readonly order?: readonly string[] | undefined;
}

declare module "effect/Schema" {
  namespace Annotations {
    interface Annotations {
      readonly providerSettingsForm?: ProviderSettingsFormAnnotation | undefined;
      readonly providerSettingsFormSchema?: ProviderSettingsFormSchemaAnnotation | undefined;
    }
  }
}

export type ProviderSettingsOrder<Fields extends Schema.Struct.Fields> = readonly Extract<
  keyof Fields,
  string
>[];

export function makeProviderSettingsSchema<const Fields extends Schema.Struct.Fields>(
  fields: Fields,
  options?: {
    readonly order?: ProviderSettingsOrder<Fields> | undefined;
  },
): Schema.Struct<Fields> {
  return Schema.Struct(fields).pipe(
    Schema.annotate({
      providerSettingsFormSchema:
        options?.order === undefined ? undefined : { order: options.order },
    }),
  );
}

export const CodexSettings = makeProviderSettingsSchema(
  {
    enabled: Schema.Boolean.pipe(
      Schema.withDecodingDefault(Effect.succeed(true)),
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
    binaryPath: makeBinaryPathSetting("codex").pipe(
      Schema.annotateKey({
        title: "Binary path",
        description: "Path to the Codex binary used by this instance.",
        providerSettingsForm: { placeholder: "codex", clearWhenEmpty: "omit" },
      }),
    ),
    homePath: TrimmedString.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "CODEX_HOME path",
        description: "Custom Codex home and config directory.",
        providerSettingsForm: {
          placeholder: "~/.codex",
          clearWhenEmpty: "omit",
        },
      }),
    ),
    shadowHomePath: TrimmedString.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "Shadow home path",
        description:
          "Account-specific Codex home. Keeps auth.json separate while sharing state from CODEX_HOME.",
        providerSettingsForm: {
          placeholder: "~/.codex-afar-research/personal",
          clearWhenEmpty: "omit",
        },
      }),
    ),
    launchArgs: TrimmedString.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "Launch arguments",
        description: "Additional CLI arguments passed to codex app-server on session start.",
      }),
    ),
    customModels: Schema.Array(Schema.String).pipe(
      Schema.withDecodingDefault(Effect.succeed([])),
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
    /**
     * Offer only `customModels`, never the models Codex itself lists. Set for a provider that
     * serves a fixed allowlist, such as AFAR Models, where Codex's own presets would be refused.
     */
    customModelsOnly: Schema.optionalKey(Schema.Boolean).pipe(
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
  },
  {
    order: ["binaryPath", "homePath", "shadowHomePath", "launchArgs"],
  },
);
export type CodexSettings = typeof CodexSettings.Type;

/**
 * Configuration for one Experimental Claude Code Agent Provider instance.
 * Authentication is intentionally absent: Claude Code owns its native login
 * and AFAR only selects the executable and configuration directory to observe.
 */
const CLAUDE_AUTO_COMPACT_WINDOW_PATTERN = /^(?:|[1-9]\d{5}|1000000)$/u;

export const ClaudeAgentSettings = makeProviderSettingsSchema(
  {
    enabled: Schema.Boolean.pipe(
      Schema.withDecodingDefault(Effect.succeed(true)),
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
    binaryPath: makeBinaryPathSetting("claude").pipe(
      Schema.annotateKey({
        title: "Claude Code executable",
        description:
          "Path to the user-installed Claude Code executable. AFAR does not install Claude Code.",
        providerSettingsForm: { placeholder: "claude", clearWhenEmpty: "omit" },
      }),
    ),
    homePath: TrimmedString.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "CLAUDE_CONFIG_DIR path",
        description:
          "Custom Claude home and config directory. Keeps .claude.json and .claude separate.",
        providerSettingsForm: { placeholder: "~/.claude", clearWhenEmpty: "omit" },
      }),
    ),
    launchArgs: TrimmedString.pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "Launch arguments",
        description: "Additional CLI arguments passed on session start.",
        providerSettingsForm: {
          placeholder: "e.g. --chrome",
          clearWhenEmpty: "omit",
        },
      }),
    ),
    autoCompactWindow: TrimmedString.check(
      Schema.isPattern(CLAUDE_AUTO_COMPACT_WINDOW_PATTERN),
    ).pipe(
      Schema.withDecodingDefault(Effect.succeed("")),
      Schema.annotateKey({
        title: "Auto-compact after",
        description:
          "Compact after 100,000 to 1,000,000 tokens. Leave empty to use Claude's default.",
        providerSettingsForm: {
          placeholder: "e.g. 300000",
          clearWhenEmpty: "omit",
        },
      }),
    ),
    customModels: Schema.Array(Schema.String).pipe(
      Schema.withDecodingDefault(Effect.succeed([])),
      Schema.annotateKey({ providerSettingsForm: { hidden: true } }),
    ),
  },
  { order: ["binaryPath", "homePath", "autoCompactWindow", "launchArgs"] },
);
export type ClaudeAgentSettings = typeof ClaudeAgentSettings.Type;

export const DEFAULT_AUTOMATIC_GIT_FETCH_INTERVAL = Duration.seconds(30);

export const MAX_ENVIRONMENT_ACCESS_TRUSTED_WEB_ORIGINS = 32;

const CanonicalEnvironmentAccessTrustedWebOrigin = Schema.String.check(
  Schema.makeFilter(
    (value) => {
      try {
        const url = new URL(value);
        return (
          (url.protocol === "https:" &&
            !url.hostname.includes("*") &&
            url.username.length === 0 &&
            url.password.length === 0 &&
            url.origin === value) ||
          "Use an exact HTTPS origin without a path, query, fragment, wildcard, or credentials"
        );
      } catch {
        return "Use an exact HTTPS origin";
      }
    },
    { identifier: "EnvironmentAccessTrustedWebOrigin" },
  ),
);

export const EnvironmentAccessTrustedWebOrigin = TrimmedNonEmptyString.pipe(
  Schema.decodeTo(
    CanonicalEnvironmentAccessTrustedWebOrigin,
    SchemaTransformation.transformOrFail({
      decode: (value) => {
        try {
          const url = new URL(value);
          const isExactOrigin =
            url.protocol === "https:" &&
            !url.hostname.includes("*") &&
            url.username.length === 0 &&
            url.password.length === 0 &&
            url.pathname === "/" &&
            url.search.length === 0 &&
            url.hash.length === 0;
          return Effect.succeed(isExactOrigin ? url.origin : value);
        } catch {
          return Effect.succeed(value);
        }
      },
      encode: (value) => Effect.succeed(value),
    }),
  ),
);
export type EnvironmentAccessTrustedWebOrigin = typeof EnvironmentAccessTrustedWebOrigin.Type;

/**
 * How the App words the Git text it writes for the user: commit messages,
 * branch names, and pull request titles and bodies. The mode picks where the
 * wording comes from; `customInstructions` only matters in `custom`.
 */
export const SourceControlWritingStyleMode = Schema.Literals([
  "repo_conventions",
  "conventional_commits",
  "custom",
]);
export type SourceControlWritingStyleMode = typeof SourceControlWritingStyleMode.Type;

export const SourceControlWritingStyleSettings = Schema.Struct({
  mode: SourceControlWritingStyleMode.pipe(
    Schema.withDecodingDefault(Effect.succeed("repo_conventions" as const)),
  ),
  customInstructions: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  followPullRequestTemplates: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
});
export type SourceControlWritingStyleSettings = typeof SourceControlWritingStyleSettings.Type;

/**
 * The Environment settings an AFAR Project may answer differently from the
 * Environment it lives on. Everything else is Environment-wide: a project
 * override only earns its place when two projects on one machine genuinely
 * want different answers.
 */
export const PROJECT_SCOPED_SERVER_SETTING_KEYS = [
  "defaultAgentModelSelection",
  "textGenerationModelSelection",
  "defaultThreadEnvMode",
  "newWorktreesStartFromOrigin",
  "agentBrowserAccess",
  "alwaysIngestAttachments",
  "responseStreamingMode",
  "autoSettleOnMerge",
  "autoSettleAfterDays",
  "pushAuthorshipNotes",
  "continueThreadsAfterServerUpdate",
  "automaticGitFetchInterval",
  "defaultAutoPull",
  "pullRequestMergeMethod",
  "sourceControlWriterModelSelection",
  "sourceControlWritingStyle",
] as const;
export type ProjectScopedServerSettingKey = (typeof PROJECT_SCOPED_SERVER_SETTING_KEYS)[number];

/**
 * One AFAR Project's overrides. An absent key is not missing information: the
 * project inherits its Environment's answer, and only a deliberate override is
 * ever stored.
 */
export const ProjectSettingsOverrides = Schema.Struct({
  defaultAgentModelSelection: Schema.optionalKey(ModelSelection),
  textGenerationModelSelection: Schema.optionalKey(ModelSelection),
  defaultThreadEnvMode: Schema.optionalKey(ThreadEnvMode),
  newWorktreesStartFromOrigin: Schema.optionalKey(Schema.Boolean),
  agentBrowserAccess: Schema.optionalKey(Schema.Boolean),
  alwaysIngestAttachments: Schema.optionalKey(Schema.Boolean),
  responseStreamingMode: Schema.optionalKey(ResponseStreamingMode),
  autoSettleOnMerge: Schema.optionalKey(Schema.Boolean),
  autoSettleAfterDays: Schema.optionalKey(Schema.NullOr(AutoSettleAfterDays)),
  pushAuthorshipNotes: Schema.optionalKey(Schema.Boolean),
  continueThreadsAfterServerUpdate: Schema.optionalKey(Schema.Boolean),
  automaticGitFetchInterval: Schema.optionalKey(Schema.DurationFromMillis),
  defaultAutoPull: Schema.optionalKey(Schema.Boolean),
  pullRequestMergeMethod: Schema.optionalKey(Schema.NullOr(PullRequestMergeMethod)),
  sourceControlWriterModelSelection: Schema.optionalKey(Schema.NullOr(ModelSelection)),
  sourceControlWritingStyle: Schema.optionalKey(SourceControlWritingStyleSettings),
} satisfies Record<ProjectScopedServerSettingKey, unknown>);
export type ProjectSettingsOverrides = typeof ProjectSettingsOverrides.Type;

const UsageModelTokenPrice = Schema.Number.check(
  Schema.isFinite(),
  Schema.isGreaterThanOrEqualTo(0),
);

/** USD per million tokens. Omitted cache rates use the input rate. */
export const UsageModelPriceOverride = Schema.Struct({
  inputCostPerMillionTokens: UsageModelTokenPrice,
  outputCostPerMillionTokens: UsageModelTokenPrice,
  cacheReadCostPerMillionTokens: Schema.optionalKey(UsageModelTokenPrice),
  cacheWriteCostPerMillionTokens: Schema.optionalKey(UsageModelTokenPrice),
});
export type UsageModelPriceOverride = typeof UsageModelPriceOverride.Type;

export const ServerSettings = Schema.Struct({
  defaultTheme: DefaultThemePreference.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  /**
   * When the environment's theme was last set, so clients can tell a re-set of
   * the same value from one they already applied: `theme set` must act even
   * when it names the theme it named before. Empty on environments provisioned
   * by builds that predate it, where clients fall back to applying once per
   * value.
   */
  defaultThemeSetAt: Schema.String.check(Schema.isMaxLength(64)).pipe(
    Schema.withDecodingDefault(Effect.succeed("")),
  ),
  /** Desired state of the supervised Computer Use Service; the service itself reports what it achieved. */
  computerUseServiceEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  /**
   * User-created Browser Profiles for this Environment's browser (ADR-0137),
   * each a Chromium user-data folder in the Environment's state. The built-in
   * Default and Incognito profiles are synthesized by `resolveBrowserProfiles`,
   * not stored here, so they cannot be renamed away or deleted.
   */
  browserProfiles: Schema.Array(BrowserProfile).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  /** Profile new tabs open under. Falls back to Default if it no longer exists. */
  browserDefaultProfileId: BrowserProfileId.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_BROWSER_PROFILE_ID)),
  ),
  agentBrowserAccess: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  /**
   * Whether files and folders attached in a thread go straight to the Ingest
   * Action instead of asking "Ingest into Brain or just attach" each time.
   */
  alwaysIngestAttachments: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  // Replaces the old `enableAssistantStreaming` boolean. Decoding drops the
  // retired key, so a prior token-streaming opt-in resets to the paragraph
  // default rather than silently staying the loudest option.
  responseStreamingMode: ResponseStreamingMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_RESPONSE_STREAMING_MODE)),
  ),
  enableProviderUpdateChecks: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  continueThreadsAfterServerUpdate: Schema.Boolean.pipe(
    Schema.withDecodingDefault(Effect.succeed(false)),
  ),
  autoSettleOnMerge: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  autoSettleAfterDays: Schema.NullOr(AutoSettleAfterDays).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  /**
   * Whether a push also pushes the Authorship Logs (`refs/notes/ai`) to the
   * remote. Off by default: pushing them publishes them off the host.
   */
  pushAuthorshipNotes: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  backgroundActivityProfile: BackgroundActivityProfile.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_BACKGROUND_ACTIVITY_PROFILE)),
  ),
  automaticGitFetchInterval: Schema.DurationFromMillis.pipe(
    Schema.withDecodingDefault(
      Effect.succeed(Duration.toMillis(DEFAULT_AUTOMATIC_GIT_FETCH_INTERVAL)),
    ),
  ),
  defaultThreadEnvMode: ThreadEnvMode.pipe(
    Schema.withDecodingDefault(Effect.succeed("local" as const satisfies ThreadEnvMode)),
  ),
  newWorktreesStartFromOrigin: Schema.Boolean.pipe(
    Schema.withDecodingDefault(Effect.succeed(false)),
  ),
  addProjectBaseDirectory: TrimmedString.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  environmentAccessTrustedWebOrigins: Schema.Array(EnvironmentAccessTrustedWebOrigin)
    .check(Schema.isMaxLength(MAX_ENVIRONMENT_ACCESS_TRUSTED_WEB_ORIGINS))
    .pipe(Schema.withDecodingDefault(Effect.succeed([]))),
  defaultAgentModelSelection: ModelSelection.pipe(
    Schema.withDecodingDefault(
      Effect.succeed({
        instanceId: ProviderInstanceId.make("codex"),
        model: DEFAULT_MODEL,
      }),
    ),
  ),
  textGenerationModelSelection: ModelSelection.pipe(
    Schema.withDecodingDefault(
      Effect.succeed({
        instanceId: ProviderInstanceId.make("codex"),
        model: DEFAULT_GIT_TEXT_GENERATION_MODEL,
      }),
    ),
  ),
  /**
   * Model for the Git text the App writes. `null` defers to
   * `textGenerationModelSelection`, so a dedicated writer is opt-in.
   */
  sourceControlWriterModelSelection: Schema.NullOr(ModelSelection).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  sourceControlWritingStyle: SourceControlWritingStyleSettings.pipe(
    Schema.withDecodingDefault(Effect.succeed({})),
  ),
  /**
   * Fast-forward the default branch in the background when the checkout has
   * nothing of its own to lose. Off by default: pulling touches the user's
   * working directory, so it is opted into, never assumed.
   */
  defaultAutoPull: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  /** `null` leaves the order the repository itself allows, unreordered. */
  pullRequestMergeMethod: Schema.NullOr(PullRequestMergeMethod).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),

  // Legacy single-instance-per-driver settings. Continues to be the source
  // of truth until `providerInstances` (below) lands per-driver migration
  // shims and the server starts hydrating instances from it. Driver-specific
  // schemas live here for the duration of the migration; once each driver
  // owns its config in its own package, this struct shrinks to nothing and
  // is removed entirely.
  providers: Schema.Struct({
    codex: CodexSettings.pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  }).pipe(Schema.withDecodingDefault(Effect.succeed({}))),
  /** Per-project answers for PROJECT_SCOPED_SERVER_SETTING_KEYS; absent means inherit. */
  projectSettingsOverrides: Schema.Record(ProjectId, ProjectSettingsOverrides).pipe(
    Schema.withDecodingDefault(Effect.succeed({})),
  ),
  mcpServers: Schema.Array(McpDefinition).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
  mcpRevision: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)).pipe(
    Schema.withDecodingDefault(Effect.succeed(0)),
  ),
  // New driver-agnostic instance map. Keyed by `ProviderInstanceId`; values
  // are `ProviderInstanceConfig` envelopes. The driver-specific config blob
  // is `Schema.Unknown` at this layer so envelopes with unknown drivers
  // (forks, downgrades, in-flight PR branches) round-trip without loss.
  // See providerInstance.ts for the forward/backward compatibility invariant.
  providerInstances: Schema.Record(ProviderInstanceId, ProviderInstanceConfig).pipe(
    Schema.withDecodingDefault(Effect.succeed({})),
  ),
  /** Exact model IDs, applied to past and future usage on this AFAR Environment. */
  usagePriceOverrides: Schema.Record(TrimmedNonEmptyString, UsageModelPriceOverride).pipe(
    Schema.withDecodingDefault(Effect.succeed({})),
  ),
  /**
   * The glyph the user picked for this Environment's machine, or null to use the one its
   * platform implies. Adapted from T3 Code's `environmentIcon` setting; kept on the Environment so
   * every client draws the same machine the same way.
   */
  environmentIcon: Schema.NullOr(EnvironmentMachineKind).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
});
export type ServerSettings = typeof ServerSettings.Type;

export const DEFAULT_SERVER_SETTINGS: ServerSettings = Schema.decodeSync(ServerSettings)({});

export const ServerSettingsOperation = Schema.Literals([
  "normalize",
  "mcp-settings",
  "read-file",
  "read-secret",
  "remove-secret",
  "remove-stale-secret",
  "write-secret",
  "write-file",
  "prepare-directory",
]);
export type ServerSettingsOperation = typeof ServerSettingsOperation.Type;

export class ServerSettingsError extends Schema.TaggedErrorClass<ServerSettingsError>()(
  "ServerSettingsError",
  {
    settingsPath: Schema.String,
    operation: ServerSettingsOperation,
    reason: Schema.optionalKey(Schema.String),
    providerInstanceId: Schema.optional(Schema.String),
    environmentVariable: Schema.optional(Schema.String),
    cause: Schema.Defect(),
  },
) {
  override get message(): string {
    if (this.operation === "mcp-settings" && this.reason) return this.reason;
    const provider =
      this.providerInstanceId === undefined ? "" : ` for provider ${this.providerInstanceId}`;
    const variable =
      this.environmentVariable === undefined
        ? ""
        : ` and environment variable ${this.environmentVariable}`;
    return `Server settings ${this.operation} failed${provider}${variable} at ${this.settingsPath}.`;
  }
}

// `computerUseServiceEnabled` is deliberately absent: any paired client may
// start the Computer Use Service (ADR-0137).
export const ServerSettingsHostOnlySetting = Schema.Literals([
  "automaticGitFetchInterval",
  "environmentAccessTrustedWebOrigins",
]);
export type ServerSettingsHostOnlySetting = typeof ServerSettingsHostOnlySetting.Type;

export class ServerSettingsHostOnlyError extends Schema.TaggedErrorClass<ServerSettingsHostOnlyError>()(
  "ServerSettingsHostOnlyError",
  {
    setting: ServerSettingsHostOnlySetting,
  },
) {
  override get message(): string {
    return `${this.setting} can only be changed from the Host Web Client or Desktop Client.`;
  }
}

/** Settings AFAR owns in a Cloud Workspace, where its one managed Codex is the only provider. */
export const ServerSettingsManagedSetting = Schema.Literals(["providerInstances", "providers"]);
export type ServerSettingsManagedSetting = typeof ServerSettingsManagedSetting.Type;

export class ServerSettingsManagedError extends Schema.TaggedErrorClass<ServerSettingsManagedError>()(
  "ServerSettingsManagedError",
  {
    setting: ServerSettingsManagedSetting,
  },
) {
  override get message(): string {
    return `${this.setting} is managed by AFAR in this Cloud Workspace.`;
  }
}

// ── Unified type ─────────────────────────────────────────────────────

export type UnifiedSettings = ServerSettings & ClientSettings;
export const DEFAULT_UNIFIED_SETTINGS: UnifiedSettings = {
  ...DEFAULT_SERVER_SETTINGS,
  ...DEFAULT_CLIENT_SETTINGS,
};

// ── Server Settings Patch (replace with a Schema.deepPartial if available) ──────────────────────────────────────────

const ModelSelectionPatch = Schema.Struct({
  instanceId: Schema.optionalKey(ProviderInstanceId),
  model: Schema.optionalKey(TrimmedNonEmptyString),
  options: Schema.optionalKey(ProviderOptionSelections),
});

const CodexSettingsPatch = Schema.Struct({
  enabled: Schema.optionalKey(Schema.Boolean),
  binaryPath: Schema.optionalKey(TrimmedString),
  homePath: Schema.optionalKey(TrimmedString),
  shadowHomePath: Schema.optionalKey(TrimmedString),
  customModels: Schema.optionalKey(Schema.Array(Schema.String)),
});

export const ServerSettingsPatch = Schema.Struct({
  computerUseServiceEnabled: Schema.optionalKey(Schema.Boolean),
  browserProfiles: Schema.optionalKey(Schema.Array(BrowserProfile)),
  browserDefaultProfileId: Schema.optionalKey(BrowserProfileId),
  environmentIcon: Schema.optionalKey(Schema.NullOr(EnvironmentMachineKind)),
  agentBrowserAccess: Schema.optionalKey(Schema.Boolean),
  alwaysIngestAttachments: Schema.optionalKey(Schema.Boolean),
  // Server settings
  responseStreamingMode: Schema.optionalKey(ResponseStreamingMode),
  enableProviderUpdateChecks: Schema.optionalKey(Schema.Boolean),
  continueThreadsAfterServerUpdate: Schema.optionalKey(Schema.Boolean),
  autoSettleOnMerge: Schema.optionalKey(Schema.Boolean),
  autoSettleAfterDays: Schema.optionalKey(Schema.NullOr(AutoSettleAfterDays)),
  pushAuthorshipNotes: Schema.optionalKey(Schema.Boolean),
  backgroundActivityProfile: Schema.optionalKey(BackgroundActivityProfile),
  automaticGitFetchInterval: Schema.optionalKey(Schema.DurationFromMillis),
  defaultThreadEnvMode: Schema.optionalKey(ThreadEnvMode),
  newWorktreesStartFromOrigin: Schema.optionalKey(Schema.Boolean),
  addProjectBaseDirectory: Schema.optionalKey(TrimmedString),
  environmentAccessTrustedWebOrigins: Schema.optionalKey(
    Schema.Array(EnvironmentAccessTrustedWebOrigin).check(
      Schema.isMaxLength(MAX_ENVIRONMENT_ACCESS_TRUSTED_WEB_ORIGINS),
    ),
  ),
  defaultAgentModelSelection: Schema.optionalKey(ModelSelectionPatch),
  textGenerationModelSelection: Schema.optionalKey(ModelSelectionPatch),
  sourceControlWriterModelSelection: Schema.optionalKey(Schema.NullOr(ModelSelection)),
  sourceControlWritingStyle: Schema.optionalKey(
    Schema.Struct({
      mode: Schema.optionalKey(SourceControlWritingStyleMode),
      customInstructions: Schema.optionalKey(TrimmedString),
      followPullRequestTemplates: Schema.optionalKey(Schema.Boolean),
    }),
  ),
  defaultAutoPull: Schema.optionalKey(Schema.Boolean),
  pullRequestMergeMethod: Schema.optionalKey(Schema.NullOr(PullRequestMergeMethod)),
  providers: Schema.optionalKey(
    Schema.Struct({
      codex: Schema.optionalKey(CodexSettingsPatch),
    }),
  ),
  projectSettingsOverrides: Schema.optionalKey(
    Schema.Record(ProjectId, Schema.NullOr(ProjectSettingsOverrides)),
  ),
  mcpServers: Schema.optionalKey(Schema.Array(McpDefinition)),
  mcpRevision: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))),
  // Whole-map replacement for the new instance config. Patching individual
  // entries is intentionally out of scope: the map is small, and partial
  // patches risk leaving driver-specific config in a half-merged state.
  // The web UI sends a fully-formed map every time it edits this field.
  providerInstances: Schema.optionalKey(Schema.Record(ProviderInstanceId, ProviderInstanceConfig)),
  // Per-entry, unlike `providerInstances`: a client only ever edits the rows it
  // touched, and sending the whole map races another edit that has not echoed
  // back yet. `null` restores automatic pricing; the server merges into its
  // current map.
  usagePriceOverrides: Schema.optionalKey(
    Schema.Record(TrimmedNonEmptyString, Schema.NullOr(UsageModelPriceOverride)),
  ),
});
export type ServerSettingsPatch = typeof ServerSettingsPatch.Type;

export const ClientSettingsPatch = Schema.Struct({
  browserDefaultViewport: Schema.optionalKey(PreviewViewportSetting),
  browserDefaultZoomFactor: Schema.optionalKey(PreviewZoomFactor),
  browserDefaultAppearance: Schema.optionalKey(PreviewAppearancePreference),
  browserRecordingFrameRate: Schema.optionalKey(BrowserRecordingFrameRate),
  browserLinkTarget: Schema.optionalKey(BrowserLinkTarget),
  browserAutoShowFloatingPreview: Schema.optionalKey(Schema.Boolean),
  panelAnimationDurationMs: Schema.optionalKey(PanelAnimationDurationMs),
  planModeEnabled: Schema.optionalKey(Schema.Boolean),
  contextWindowMeterEnabled: Schema.optionalKey(Schema.Boolean),
  composerCollapseOnScroll: Schema.optionalKey(Schema.Boolean),
  composerRichTextEnabled: Schema.optionalKey(Schema.Boolean),
  sendShortcut: Schema.optionalKey(Schema.Literals(["enter", "mod-enter-multiline", "mod-enter"])),
  showSkillsInSlashMenu: Schema.optionalKey(Schema.Boolean),
  sidebarProjectScopeKey: Schema.optionalKey(Schema.NullOr(Schema.String)),
  projectPresentation: Schema.optionalKey(Schema.Record(Schema.String, ProjectPresentation)),
  browserPreferences: Schema.optionalKey(BrowserPreferences),
  appearanceContrast: Schema.optionalKey(AppearanceContrast),
  glassOpacity: Schema.optionalKey(GlassOpacity),
  diffColorScheme: Schema.optionalKey(DiffColorScheme),
  fontSizeInterface: Schema.optionalKey(InterfaceFontSize),
  fontSizePrompt: Schema.optionalKey(PromptFontSize),
  fontSizeCode: Schema.optionalKey(CodeFontSize),
  fontSizeTerminal: Schema.optionalKey(TerminalFontSize),
  fontFamilySans: Schema.optionalKey(FontFamilyPreference),
  fontFamilyCode: Schema.optionalKey(FontFamilyPreference),
  fontFamilyComposer: Schema.optionalKey(FontFamilyPreference),
  fontFamilyTerminal: Schema.optionalKey(FontFamilyPreference),
  fontSmoothing: Schema.optionalKey(Schema.Boolean),
  autoOpenPlanSidebar: Schema.optionalKey(Schema.Boolean),
  confirmThreadArchive: Schema.optionalKey(Schema.Boolean),
  confirmThreadDelete: Schema.optionalKey(Schema.Boolean),
  confirmThreadUnpin: Schema.optionalKey(Schema.Boolean),
  diffIgnoreWhitespace: Schema.optionalKey(Schema.Boolean),
  diffFilesCollapsed: Schema.optionalKey(Schema.Boolean),
  inAppNotificationsEnabled: Schema.optionalKey(Schema.Boolean),
  proactivePanelsEnabled: Schema.optionalKey(Schema.Boolean),
  followUpBehavior: Schema.optionalKey(FollowUpBehavior),
  confirmQuit: Schema.optionalKey(QuitConfirmationMode),
  diffLayout: Schema.optionalKey(DiffLayout),
  environmentSettingsSelectionId: Schema.optionalKey(Schema.NullOr(EnvironmentId)),
  favorites: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        provider: ProviderInstanceId,
        model: TrimmedNonEmptyString,
      }),
    ),
  ),
  providerModelPreferences: Schema.optionalKey(
    Schema.Record(
      ProviderInstanceId,
      Schema.Struct({
        hiddenModels: Schema.Array(Schema.String).pipe(
          Schema.withDecodingDefault(Effect.succeed([])),
        ),
        modelOrder: Schema.Array(Schema.String).pipe(
          Schema.withDecodingDefault(Effect.succeed([])),
        ),
      }),
    ),
  ),
  pinnedThreadRefs: Schema.optionalKey(Schema.Array(ScopedThreadRef)),
  sidebarProjectGroupingMode: Schema.optionalKey(SidebarProjectGroupingMode),
  sidebarProjectGroupingOverrides: Schema.optionalKey(
    Schema.Record(TrimmedNonEmptyString, SidebarProjectGroupingMode),
  ),
  sidebarProjectSortOrder: Schema.optionalKey(SidebarProjectSortOrder),
  sidebarThreadSortOrder: Schema.optionalKey(SidebarThreadSortOrder),
  sidebarThreadPreviewCount: Schema.optionalKey(SidebarThreadPreviewCount),
  timestampFormat: Schema.optionalKey(TimestampFormat),
  snapShotEnabled: Schema.optionalKey(Schema.Boolean),
  snapShotIncludeAccessibility: Schema.optionalKey(Schema.Boolean),
  snapShotShortcut: Schema.optionalKey(SnapShotShortcut),
  snapShotPlaySound: Schema.optionalKey(Schema.Boolean),
  snapShotSound: Schema.optionalKey(SnapShotSound),
  snapShotFlash: Schema.optionalKey(Schema.Boolean),
  snapShotAnimations: Schema.optionalKey(Schema.Boolean),
  wordWrap: Schema.optionalKey(Schema.Boolean),
  loadBalancingEnabled: Schema.optionalKey(Schema.Boolean),
  loadBalancingPreferences: Schema.optionalKey(
    Schema.Record(Schema.String, LoadBalancingPreferenceSetting),
  ),
  gitHubRoutingPermissions: Schema.optionalKey(Schema.Array(GitHubRoutingPermissionSetting)),
  disabledEnvironmentIds: Schema.optionalKey(Schema.Array(EnvironmentId)),
  connectEnvironmentIdsByAccount: Schema.optionalKey(
    Schema.Record(Schema.String, Schema.Array(EnvironmentId)),
  ),
  sshConnections: Schema.optionalKey(Schema.Array(SshConnectionSetting)),
  pairedConnections: Schema.optionalKey(Schema.Array(PairedConnectionSetting)),
});
export type ClientSettingsPatch = typeof ClientSettingsPatch.Type;
